/**
 * Height of the DESKTOP widget, and the ceiling for the mobile sheet.
 *
 * Exported so `ConciergeWidget`'s desktop branch uses this same constant: the
 * mobile sheet is capped at the desktop height by request, and two copies of the
 * number would drift apart the first time either is retuned.
 */
export const DESKTOP_SHEET_HEIGHT_PX = 793;

/**
 * Share of the visible area the sheet occupies, leaving the rest as a peek at
 * the page so it reads as a sheet over a page rather than a new screen.
 *
 * A FRACTION, not a pixel inset. The old 80px peek was a fixed number chosen
 * against one screen; a share scales from a phone with the keyboard up (~450px
 * visible) to a tablet without it.
 */
const SHEET_VISIBLE_FRACTION = 0.9;

/**
 * How far below the sheet's own bottom edge the skirt reaches.
 *
 * The skirt is a strip of the sheet's colour behind the sheet, so a shortfall
 * between the sheet's edge and the true visible bottom shows as sheet-white
 * rather than dimmed page. Residuals measured on the device were ~43px; this
 * leaves generous room without being visible when there is no shortfall (the
 * keyboard covers it).
 */
export const SKIRT_DROP_PX = 240;

/** What the sheet needs to know about the viewport. */
export interface MobileSheetViewport {
  /**
   * `visualViewport.height` — the area the user can actually see.
   *
   * The only viewport number that proved trustworthy on the device: 448 with the
   * keyboard up, 755-836 without it, every time, while `innerHeight` swung
   * between 448 and 796 in the same state. Because it already shrinks for the
   * keyboard, sizing from it needs no keyboard detection at all — which retires
   * a whole class of bug (see the test file for the readings that killed three
   * different detection attempts).
   */
  visibleHeight: number;
  /**
   * `innerHeight - visualViewport.height - offsetTop`, clamped at 0 — how far
   * below the visible bottom a `bottom: 0` sheet would land.
   *
   * Load-bearing, and measured on a deterministic repro (open, close, reopen):
   * iOS shrinks the LAYOUT viewport for the keyboard on the first open
   * (`innerHeight == vv.height == 449`, so this is 0 and `bottom: 0` is already
   * flush) and does NOT on the second (`innerHeight 796`, `offsetTop 0`, so this
   * is 347 and `bottom: 0` puts the input box behind the keyboard). Same
   * keyboard, same screen, same reported `vv.height` — only the layout viewport
   * differs, which is exactly what this expression captures.
   */
  keyboardInset: number;
}

/** The vertical box of the mobile sheet: where its bottom sits and how tall it is. */
export interface MobileSheetGeometry {
  /** px to lift the sheet so its bottom edge lands on the visible bottom. */
  bottom: number;
  height: string;
  maxHeight: string;
}

/**
 * The sheet's vertical box.
 *
 * **Placement.** `bottom: keyboardInset`. iOS anchors a fixed bottom edge to the
 * LAYOUT viewport, which coincides with the visible bottom only when iOS shrank
 * the layout viewport for the keyboard — which it does on the first open and not
 * on the second. The inset is the difference, and lands the sheet on the visible
 * bottom in both. It was briefly removed because a SMALL residual (~42px) showed
 * dimmed page under the input box; that is what the skirt behind the sheet is
 * for, and removing the lift instead cost 347px of misplacement on every reopen.
 *
 * **Height.** A share of the visible area, capped at the desktop height. No
 * keyboard test: `vv.height` has already shrunk when the keyboard is up, so the
 * same expression gives a near-full sheet with the keyboard down and a sheet
 * sitting on the keyboard when it is up. Every previous version asked the
 * viewport whether the keyboard was up and got a different answer on every tap,
 * which is what made the sheet a different size each time.
 *
 * `visibleHeight` is 0 only before the first measurement (SSR, no
 * `visualViewport`), where a vh expression is the best available answer.
 */
export function mobileSheetGeometry({ visibleHeight, keyboardInset }: MobileSheetViewport): MobileSheetGeometry {
  const height = visibleHeight
    ? `${Math.min(Math.round(visibleHeight * SHEET_VISIBLE_FRACTION), DESKTOP_SHEET_HEIGHT_PX)}px`
    : `min(${SHEET_VISIBLE_FRACTION * 100}vh, ${DESKTOP_SHEET_HEIGHT_PX}px)`;
  return { bottom: keyboardInset, height, maxHeight: height };
}
